import { ICreateBayGridPartProps, ISvgWithSize } from "../../types/ICreateBayGridBaseProps";
import { ISlotCombinedPattern, slotIdJoinedToMixed } from "@tedivo/tedivo-bay-grid-pure";

import { ISlotData } from "open-vessel-definition";
import { SYMBOL_NAMES } from "../consts";
import { calculateGridPartDimensions } from "./calculateGridDimensions";
import { createSvgText } from "../../shipViews/helpers/createSvgText";

/** Font used for the small Bottom Base / TCG numbers, matching the aft-view diagram's CG numbers. */
const CG_LABEL_FONT_FAMILY = `"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace`;
const CG_LABEL_LETTER_SPACING = "-0.05em";
/** Vertical position (as a ratio of a cell's height) of the TIER/ROW number when a CG label is shown beneath it. */
const NUMBER_SHIFTED_UP_Y_RATIO = 0.32;
/** Vertical position (as a ratio of a cell's height) of the small CG label. */
const CG_LABEL_Y_RATIO = 0.74;

/**
 * Creates a Grid with labels. For instance, the above deck. Used by other functions.
 * @returns ISvgWithSize { svgGroup, width, height, symbols }
 */
export default function createGridPart<U>({
  tiersArray,
  rowsArray,
  enabledCells,
  prohibitedCells,
  containerWidth,
  containerHeight,
  displayNumbers,
  cellSeparation = 0,
  nodesOptions,
  symbolPrefix = "",
  cellsToDraw,
  emphasizeCells,
  cellsDrawFunction,
  emphasizeCellsDrawFunction,
  showBottomBases = false,
  showTCGs = false,
  bottomBaseLabelsByTier,
  tcgLabelsByRow,
}: ICreateBayGridPartProps<U>): ISvgWithSize {
  const mainSvgPaths: string[] = [];
  const svgGroup = document.createElementNS("http://www.w3.org/2000/svg", "g");
  const allSymbols: { [id: string]: SVGSymbolElement } = {};

  const {
    boxDisabledBackgroundColor = "#ddd",
    boxDisabledBorderColor = "#aaa",
    boxProhibitedBorderColor = "#aaa",
    boxBackgroundColor = "#f4f4f4",
    boxBorderColor = "#ccc",
    lineColor = "#ccc",
    gridNumbersFontSize = 9,
    gridNumbersTextColor = "#777",
    cgNumbersFontSize = 5,
    cgNumbersTextColor = gridNumbersTextColor,
    bottomBaseNumbersColor = cgNumbersTextColor,
    tcgPortNumbersColor = cgNumbersTextColor,
    tcgStbdNumbersColor = cgNumbersTextColor,
  } = nodesOptions;

  const createSvgLinePath = (x1: number, y1: number, x2: number, y2: number) => {
    mainSvgPaths.push("M" + x1 + "," + y1 + " " + x2 + "," + y2);
  };

  // Dimensions
  const {
    numberOfTiers,
    numberOfRows,
    addLeftWidth,
    addTopHeight,
    widthOfCells,
    heightOfCells,
    widthOfGrid,
    heightOfGrid,
    cellPositions,
  } = calculateGridPartDimensions({
    tiersArray,
    rowsArray,
    enabledCells,
    containerWidth,
    containerHeight,
    displayNumbers,
    cellSeparation,
  });

  const empasizeMap = createEmphasizeMap(emphasizeCells);

  // Boxes for each cell
  for (let t = 0; t < numberOfTiers; t += 1) {
    for (let c = 0; c < numberOfRows; c += 1) {
      const posComb = `${rowsArray[c]}|${tiersArray[t]}` as ISlotCombinedPattern;
      const cellIsDisabled = enabledCells && enabledCells.indexOf(posComb) < 0;
      const cellIsProhib = prohibitedCells && prohibitedCells.indexOf(posComb) >= 0;

      const { x, y } = cellPositions[posComb];

      const symbolName = cellIsProhib
        ? `#${symbolPrefix}${SYMBOL_NAMES.PROHIBITED}`
        : cellIsDisabled
        ? `#${symbolPrefix}${SYMBOL_NAMES.DISABLED}`
        : `#${symbolPrefix}${SYMBOL_NAMES.REGULAR}`;

      const box = document.createElementNS("http://www.w3.org/2000/svg", "use");
      box.setAttribute("href", symbolName);
      box.setAttribute("x", String(x));
      box.setAttribute("y", String(y));

      box.dataset["pos"] = posComb;
      svgGroup.appendChild(box);

      if (empasizeMap.has(posComb) && emphasizeCellsDrawFunction) {
        const slotData = empasizeMap.get(posComb);
        const { node: emph, nodeLabel: emphLabel } = emphasizeCellsDrawFunction(
          posComb,
          slotData as U,
        );

        emph.setAttribute("x", String(x));
        emph.setAttribute("y", String(y));
        svgGroup.appendChild(emph);

        if (emphLabel) {
          emphLabel.setAttribute("transform", `translate(${x}, ${y + containerHeight + 4})`);
          svgGroup.appendChild(emphLabel);
        }
      }
    }
  }

  //draw horizontal lines & numbers
  for (let j = 0; j <= numberOfTiers; j += 1) {
    if (!cellSeparation)
      createSvgLinePath(
        addLeftWidth,
        addTopHeight + containerHeight * j,
        addLeftWidth + widthOfCells,
        addTopHeight + containerHeight * j,
      );

    const isoTier = tiersArray[j];
    const bottomBaseLabel = showBottomBases ? bottomBaseLabelsByTier?.[isoTier || ""] : undefined;

    const tierRowBaseY = addTopHeight + (containerHeight + cellSeparation) * j;
    const tierNumberY =
      tierRowBaseY + containerHeight * (showBottomBases ? NUMBER_SHIFTED_UP_Y_RATIO : 0.5);
    const bottomBaseY = tierRowBaseY + containerHeight * CG_LABEL_Y_RATIO;

    if (displayNumbers.left && j < numberOfTiers) {
      appendSvgText(isoTier || "", containerWidth * 0.5, tierNumberY);
      if (bottomBaseLabel)
        appendCgText(bottomBaseLabel, containerWidth * 0.5, bottomBaseY, bottomBaseNumbersColor);
    }

    if (displayNumbers.right && j < numberOfTiers) {
      const x = addLeftWidth + widthOfCells + containerWidth * 0.5;
      appendSvgText(isoTier || "", x, tierNumberY);
      if (bottomBaseLabel) appendCgText(bottomBaseLabel, x, bottomBaseY, bottomBaseNumbersColor);
    }
  }

  //draw vertical lines & numbers
  for (let j = 0; j <= numberOfRows; j += 1) {
    if (!cellSeparation)
      createSvgLinePath(
        addLeftWidth + containerWidth * j,
        addTopHeight,
        addLeftWidth + containerWidth * j,
        addTopHeight + heightOfCells,
      );

    const isoRow = rowsArray[j];
    const tcgLabel = showTCGs ? tcgLabelsByRow?.[isoRow || ""] : undefined;
    const x = addLeftWidth + (containerWidth + cellSeparation) * j + containerWidth * 0.5;
    const tcgColor = Number(isoRow) % 2 === 0 ? tcgPortNumbersColor : tcgStbdNumbersColor;

    if (displayNumbers.top && j < numberOfRows) {
      appendSvgText(isoRow || "", x, containerHeight * (showTCGs ? NUMBER_SHIFTED_UP_Y_RATIO : 0.5));
      if (tcgLabel) appendCgText(tcgLabel, x, containerHeight * CG_LABEL_Y_RATIO, tcgColor);
    }

    if (displayNumbers.bottom && j < numberOfRows) {
      const rowLabelsBaseY = addTopHeight + heightOfCells;

      appendSvgText(
        isoRow || "",
        x,
        rowLabelsBaseY + containerHeight * (showTCGs ? NUMBER_SHIFTED_UP_Y_RATIO : 0.5),
      );
      if (tcgLabel)
        appendCgText(tcgLabel, x, rowLabelsBaseY + containerHeight * CG_LABEL_Y_RATIO, tcgColor);
    }
  }

  // Add grid lines if no cell-separation
  if (!cellSeparation) {
    const svgL = document.createElementNS("http://www.w3.org/2000/svg", "path");
    svgL.setAttribute("stroke", lineColor);
    svgL.setAttribute("stroke-width", "1");
    svgL.setAttribute("class", "grid-lines");
    svgL.setAttribute("stroke-linecap", "round");
    svgL.setAttribute("d", mainSvgPaths.join(" "));
    svgGroup.appendChild(svgL);
  }

  svgGroup.setAttribute("width", String(widthOfGrid));
  svgGroup.setAttribute("height", String(heightOfGrid));

  // Add Cells
  if (cellsToDraw?.length && cellsDrawFunction) {
    const svgCellsGroup = document.createElementNS("http://www.w3.org/2000/svg", "g");
    svgCellsGroup.setAttribute("class", "cells-group");

    cellsToDraw.forEach((cellInfo) => {
      const { node, pos, symbolsCache } = cellsDrawFunction(cellInfo);
      const cellPosition = cellPositions[pos];
      if (pos && cellPosition && node) {
        svgCellsGroup.appendChild(node);
        node.setAttribute("transform", `translate(${cellPosition.x}, ${cellPosition.y})`);
      }

      if (symbolsCache) {
        symbolsCache.forEach((symbol, key) => {
          if (symbol) allSymbols[key] = symbol;
        });
      }
    });

    svgGroup.appendChild(svgCellsGroup);
  }

  for (const key in allSymbols) {
    allSymbols[key] = allSymbols[key].cloneNode(true) as SVGSymbolElement;
  }

  return {
    svgGroup,
    width: widthOfGrid,
    height: heightOfGrid,
    symbols: { ...allSymbols, ...createSymbols() },
    cellPositions,
  };

  function createSymbols() {
    const symbols: { [id: string]: SVGSymbolElement } = {};

    // 1. Regular box
    const svgBoxSymbol = document.createElementNS("http://www.w3.org/2000/svg", "symbol");

    const rect = document.createElementNS("http://www.w3.org/2000/svg", "path");
    rect.setAttribute(
      "d",
      `M${0},${0} h${containerWidth} v${containerHeight} h-${containerWidth} z`,
    );
    rect.setAttribute("fill", boxBackgroundColor);
    rect.setAttribute("stroke-width", "0");
    rect.setAttribute("data-enabled", "1");

    if (cellSeparation) {
      rect.setAttribute("stroke", boxBorderColor);
      rect.setAttribute("stroke-width", "1");
    }

    svgBoxSymbol.id = `${symbolPrefix}${SYMBOL_NAMES.REGULAR}`;
    svgBoxSymbol.setAttribute("style", "overflow:visible");
    svgBoxSymbol.appendChild(rect);

    symbols[SYMBOL_NAMES.REGULAR] = svgBoxSymbol;

    // 2. Disabled box
    const svgBoxDisabledSymbol = document.createElementNS("http://www.w3.org/2000/svg", "symbol");

    const rectD = document.createElementNS("http://www.w3.org/2000/svg", "path");
    rectD.setAttribute(
      "d",
      `M${0},${0} h${containerWidth} v${containerHeight} h-${containerWidth} z`,
    );
    rectD.setAttribute("fill", boxDisabledBackgroundColor);
    rectD.setAttribute("stroke-width", "0");
    rectD.setAttribute("data-enabled", "0");

    if (cellSeparation) {
      rectD.setAttribute("stroke", boxDisabledBorderColor);
      rectD.setAttribute("stroke-width", "0.5");
    }

    svgBoxDisabledSymbol.id = `${symbolPrefix}${SYMBOL_NAMES.DISABLED}`;
    svgBoxDisabledSymbol.setAttribute("style", "overflow:visible");
    svgBoxDisabledSymbol.appendChild(rectD);

    symbols[SYMBOL_NAMES.DISABLED] = svgBoxDisabledSymbol;

    // 3. Emphasized box
    const svgEmphasizedBox = document.createElementNS("http://www.w3.org/2000/svg", "symbol");

    const rectE = document.createElementNS("http://www.w3.org/2000/svg", "path");
    rectE.setAttribute(
      "d",
      `M${0},${0} h${containerWidth} v${containerHeight} h-${containerWidth} z`,
    );
    rectE.setAttribute("data-enabled", "0");
    rectE.setAttribute("fill-opacity", "0");
    rectE.setAttribute("stroke", nodesOptions.emphasizeLineColor || "#000");
    rectE.setAttribute("stroke-width", "2");

    svgEmphasizedBox.id = `${symbolPrefix}${SYMBOL_NAMES.EMPHASIZED}`;
    svgEmphasizedBox.setAttribute("style", "overflow:visible");
    svgEmphasizedBox.appendChild(rectE);

    symbols[SYMBOL_NAMES.EMPHASIZED] = svgEmphasizedBox;

    // 4. Prohibited box
    const svgBoxProhibitedSymbol = document.createElementNS("http://www.w3.org/2000/svg", "symbol");

    const rectDProh = document.createElementNS("http://www.w3.org/2000/svg", "path");
    rectDProh.setAttribute(
      "d",
      `M0,0 
       h${containerWidth} v${containerHeight} h-${containerWidth} L0,0 M0,0
       L${containerWidth},${containerHeight} M${containerWidth},0 L0,${containerHeight} z`,
    );
    rectDProh.setAttribute("fill", boxDisabledBackgroundColor);
    rectDProh.setAttribute("fill-opacity", "0.65");
    rectDProh.setAttribute("stroke", boxProhibitedBorderColor);
    rectDProh.setAttribute("stroke-width", "1");
    rectDProh.setAttribute("stroke-linecap", "round");
    rectDProh.setAttribute("data-enabled", "0");

    if (cellSeparation) {
      rectDProh.setAttribute("stroke", boxProhibitedBorderColor);
      rectDProh.setAttribute("stroke-width", "1");
    }

    svgBoxProhibitedSymbol.id = `${symbolPrefix}${SYMBOL_NAMES.PROHIBITED}`;
    svgBoxProhibitedSymbol.setAttribute("style", "overflow:visible; cursor: not-allowed;");
    svgBoxProhibitedSymbol.appendChild(rectDProh);

    symbols[SYMBOL_NAMES.PROHIBITED] = svgBoxProhibitedSymbol;
    return symbols;
  }

  function appendSvgText(text: string, x: number, y: number) {
    const svgT = createSvgText({
      text,
      x,
      y,
      fontSize: gridNumbersFontSize,
      textColor: gridNumbersTextColor,
      className: "st-lab",
    });
    svgGroup.appendChild(svgT);
  }

  /** Appends a small Bottom Base / TCG value, styled like the aft-view diagram's CG numbers. */
  function appendCgText(text: string, x: number, y: number, color: string) {
    const svgT = createSvgText({
      text,
      x,
      y,
      fontSize: cgNumbersFontSize,
      textColor: color,
      className: "st-lab-cg",
      fontFamily: CG_LABEL_FONT_FAMILY,
      letterSpacing: CG_LABEL_LETTER_SPACING,
    });
    svgGroup.appendChild(svgT);
  }
}

function createEmphasizeMap(emphasizeCells: ISlotCombinedPattern[] | ISlotData[] | undefined) {
  const empasizeMap = new Map<ISlotCombinedPattern, ISlotData | undefined>();
  if (emphasizeCells) {
    emphasizeCells.forEach((slotKeyOrData) => {
      if (typeof slotKeyOrData === "string") {
        empasizeMap.set(slotKeyOrData, undefined);
      } else {
        empasizeMap.set(slotIdJoinedToMixed(slotKeyOrData.pos), slotKeyOrData);
      }
    });
  }

  return empasizeMap;
}
